import {Injectable} from "@angular/core";

import {ApiClientConfiguration} from "framework/services/api.client";
import {UtilsService} from "framework/services/utils.service";
import {ChartColorUtilities} from "framework/ui/charting/core/colors";

import {Subject} from "rxjs";

@Injectable()
export class ErrorService
{
    public readonly newNotification    = new Subject<Notification>;
    public readonly clearNotifications = new Subject<void>();

    constructor(private configuration: ApiClientConfiguration)
    {}

    defaultError(message: string)
    {
        this.error(null, message);
    }

    async performActionWithErrors<T>(action: () => Promise<T>,
                                     successLabel?: string,
                                     errorLabel?: string): Promise<T>
    {
        let error: string;
        let result: T;
        try
        {
            result = await action();
        }
        catch (e)
        {
            error = e.message;
        }

        if (result)
        {
            if (successLabel) this.success(successLabel, -1);
        }
        else
        {
            this.error(null, errorLabel || error, errorLabel ? error : undefined);
        }

        return result;
    }

    error(code: string,
          label: string,
          description?: string)
    {
        // set the label to the code if no label is provided
        if (!label && code) label = code;

        const dismissTime = UtilsService.clamp(8, 20, 6 * Math.log(label.length / 16));
        this.createNotification(Notification.newErrorInstance(label, {description: description}), dismissTime * 1_000, null);
    }

    warn(label: string,
         dismissTime: number,
         description?: string)
    {
        if (dismissTime === -1) dismissTime = 7_000;

        this.createNotification(Notification.newWarningInstance(label, {description: description}), dismissTime, null);
    }

    success(label: string,
            dismissTime: number,
            description?: string,
            action?: () => Promise<boolean>)
    {
        if (dismissTime === -1) dismissTime = 4_000;

        this.createNotification(Notification.newSuccessInstance(label, {description: description}), dismissTime, action);
    }

    notify(label: string,
           dismissTime?: number,
           action?: () => Promise<boolean>,
           description?: string)
    {
        if (dismissTime === -1) dismissTime = 7_000;
        
        this.createNotification(Notification.newInformationalInstance(label, {description: description}), dismissTime, action);
    }

    createNotification(config: Notification,
                       dismissTime: number,
                       action: () => Promise<boolean>)
    {
        config.dismissTime = dismissTime;
        config.action      = action;

        this.newNotification.next(config);
    }

    dismiss()
    {
        this.clearNotifications.next();
    }

    async execAndReportError(callback: () => Promise<void>)
    {
        try
        {
            await callback();
        }
        catch (error)
        {
            if (error.code)
            {
                this.error(error.code, error.message);
            }
            else
            {
                this.error("Error", `${JSON.stringify(error)}`);
            }

            error.csErrorHandled = true;
            throw error;
        }
    }

    async execAndReportErrorFromDecorator(method: Function,
                                          target: any,
                                          args: IArguments): Promise<any>
    {
        try
        {
            return await method.apply(target, args);
        }
        catch (error)
        {
            if (error.validationErrors)
            {
                const details = (<any[]>error.validationErrors.entries).map((entry) => `'${entry.field}' : ${entry.reason}`);
                const message = details.length ? error.message + "\n\n" + details.join("\n") : error.message;
                this.error(error.code, message);
            }
            else if (error.code)
            {
                if (this.configuration.isNotAuthenticatedError(error.code))
                {
                    this.configuration.handleNotAuthenticatedError();
                }
                else
                {
                    this.error(error.code, error.message);
                }
            }
            else
            {
                this.error("Error", `${error}`);
            }
            error.csErrorHandled = true;

            throw error;
        }
    }
}

export class Notification
{
    private static s_id: number = 0;
    public readonly id: number  = Notification.s_id++;

    action: () => Promise<boolean>;
    dismissTime: number;

    private constructor(public readonly label: string,
                public readonly color: string,
                public readonly leftIcon: string,
                public readonly description?: string,
                public readonly backgroundColor: string = "white",
                public readonly rightIcon?: string)
    {
        this.color ||= ChartColorUtilities.getColorById("CoreStack", "coreStackBlue").color;
        if (!this.backgroundColor) this.backgroundColor = ChartColorUtilities.lighterBackgroundColor(this.color);
    }

    public static newErrorInstance(label: string,
                                   notification?: Partial<Notification>): Notification
    {
        notification ||= {};
        return new Notification(label,
                                notification.color || ChartColorUtilities.getColorById("Severity Colors", "critical").color,
                                notification.leftIcon || "cs-icon-close-square",
                                notification.description, notification.backgroundColor, notification.rightIcon);
    }

    public static newWarningInstance(label: string,
                                     notification?: Partial<Notification>): Notification
    {
        notification ||= {};
        return new Notification(label,
                                notification.color || ChartColorUtilities.getColorById("Severity Colors", "high").color,
                                notification.leftIcon || "cs-icon-danger",
                                notification.description, notification.backgroundColor, notification.rightIcon);
    }

    public static newSuccessInstance(label: string,
                                     notification?: Partial<Notification>): Notification
    {
        notification ||= {};
        return new Notification(label,
                                notification.color || ChartColorUtilities.getColorById("Severity Colors", "low").color,
                                notification.leftIcon || "cs-icon-notification-bell-line",
                                notification.description, notification.backgroundColor, notification.rightIcon);
    }

    public static newInformationalInstance(label: string,
                                           notification?: Partial<Notification>): Notification
    {
        notification ||= {};
        return new Notification(label,
                                notification.color || ChartColorUtilities.getColorById("CoreStack", "coreStackBlue").color,
                                notification.leftIcon || "cs-icon-notification-bell-line",
                                notification.description, notification.backgroundColor, notification.rightIcon);
    }
}
